import {
	apiGetBiDashboardQueryKey,
	apiGetBiMetricQueryKey,
	useApiRetrieveReferencedObjectDetail,
} from '@repo/api-codegen';
import { useMount } from 'ahooks';
import { observer } from 'mobx-react-lite';
import { Suspense, useEffect } from 'react';
import { queryClient } from '../../api';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { ReferencedObjectCard } from './components/ReferencedObjectCard';
import { ReferencedObjectCardSkeleton } from './components/ReferencedObjectCardSkeleton';
import type { AiReferencedObjectCardControllerProps } from './types';

const AiReferencedObjectCardController = observer(
	({
		referencedObject,
		onReferencedObjectClick,
		promptId,
		messageId,
		shouldAutoOpenReferencedObjects,
		isLastMessage,
		shouldShowInlineReferencedObject,
		index = 0,
	}: AiReferencedObjectCardControllerProps) => {
		const { data: referencedObjectDetail } =
			useApiRetrieveReferencedObjectDetail({
				pathParams: {
					promptId,
					objectId: referencedObject.id,
				},
			});

		// Check if this message should auto-open its referenced objects
		// Only auto-open the first eligible referenced object (index 0)
		useEffect(() => {
			if (
				shouldAutoOpenReferencedObjects &&
				messageId &&
				(referencedObject.object_type === 'BI_METRIC' ||
					referencedObject.object_type === 'BI_DASHBOARD') &&
				onReferencedObjectClick &&
				isLastMessage &&
				index === 0 && // Only auto-open the first referenced object
				shouldShowInlineReferencedObject?.(referencedObject) // Don't auto-open if already open
			) {
				// Auto-open with a small delay to ensure UI is ready
				setTimeout(() => {
					onReferencedObjectClick(referencedObject);
				}, 200);
			}
		}, [
			referencedObject,
			onReferencedObjectClick,
			messageId,
			shouldAutoOpenReferencedObjects,
			isLastMessage,
			index,
			shouldShowInlineReferencedObject,
		]);

		// Handle query invalidation for updated objects
		useMount(() => {
			if (
				referencedObject.object_type === 'BI_METRIC' &&
				referencedObject.action === 'UPDATE'
			) {
				queryClient.invalidateQueries({
					queryKey: apiGetBiMetricQueryKey({
						pathParams: { biMetricId: referencedObject.object_id },
					}),
				});
			}

			if (
				referencedObject.object_type === 'BI_DASHBOARD' &&
				referencedObject.action === 'UPDATE'
			) {
				queryClient.invalidateQueries({
					queryKey: apiGetBiDashboardQueryKey({
						pathParams: { biDashboardId: referencedObject.object_id },
					}),
				});
			}
		});

		return (
			<ReferencedObjectCard
				referencedObject={referencedObject}
				referencedObjectDetail={referencedObjectDetail}
				onReferencedObjectClick={onReferencedObjectClick}
				shouldShowInlineReferencedObject={shouldShowInlineReferencedObject}
			/>
		);
	}
);

export function AiReferencedObjectCard(
	props: AiReferencedObjectCardControllerProps
) {
	return (
		<ErrorBoundary>
			<Suspense fallback={<ReferencedObjectCardSkeleton />}>
				<AiReferencedObjectCardController {...props} />
			</Suspense>
		</ErrorBoundary>
	);
}
